import { useRef, useState, type ReactNode } from 'react'
import { LogOut } from 'lucide-react'
import { ContentMain, InleagueStatusBanner, useShellTitle } from '@region13/ui'
import { COACH_NAV } from '../lib/nav'
import { Masthead } from './Masthead'
import { BottomNav } from './BottomNav'
import { Sidebar } from './Sidebar'

/** Signed-in frame. Desktop (md+): left Sidebar + main content (a centered
 *  max-w-2xl column). Mobile (<md): branded Masthead + full-width content +
 *  fixed BottomNav, whose More tab opens the Sidebar as a drawer (this frame
 *  owns its open state). Both nav layers are in the DOM; CSS shows one per
 *  breakpoint. Unlike referee-spa's AppShell, there is no safety-cert banner
 *  (that's a referee-only InLeague concept) and no wide-table route. */
export function AppShell({
  email,
  onSignOut,
  isAdmin = false,
  children,
}: {
  email: string
  onSignOut: () => void
  /** Superadmin "view as coach": the team list shows ALL teams, not this
   *  user's own. Surface a banner so it isn't mistaken for a real coach's list. */
  isAdmin?: boolean
  children: ReactNode
}) {
  useShellTitle(COACH_NAV)
  const [navOpen, setNavOpen] = useState(false)
  const moreRef = useRef<HTMLButtonElement>(null)
  return (
    <div className="flex min-h-screen flex-col bg-canvas md:flex-row">
      <Sidebar
        email={email}
        onSignOut={onSignOut}
        drawer={{ open: navOpen, onOpenChange: setNavOpen, returnFocusTo: () => moreRef.current }}
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <Masthead
          className="md:hidden"
          headerRight={
            <button
              onClick={onSignOut}
              aria-label="Sign out"
              title={`Signed in as ${email}`}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-sidebar-ink hover:text-white"
            >
              <LogOut size={20} />
            </button>
          }
        />
        {isAdmin ? (
          <div role="status" className="bg-brand-gold/20 px-4 py-2 text-center text-sm font-medium text-brand-maroon">
            Admin view: showing all teams
          </div>
        ) : null}
        <ContentMain className="mx-auto w-full max-w-2xl px-4 py-5 pb-20 md:pb-8">
          <InleagueStatusBanner className="mb-4" />
          {children}
        </ContentMain>
        <BottomNav moreOpen={navOpen} onMore={() => setNavOpen(true)} moreRef={moreRef} />
      </div>
    </div>
  )
}
